// Shared presentation helpers for transaction rows. Colocated with the
// transaction types they're keyed on. Previously AllTransactions, TransactionDetail
// and NeedsAttention each carried their own identical copy of formatAmount, and
// STATUS_BADGE lived only in AllTransactions — so the same status rendered as a
// colored pill on one page and bare text on another.

import type { AdminTransactionRow, TransactionStatus } from "./types";

/** Rendered where a legacy row has no value (amount, currency, date). */
export const MISSING_VALUE = "n/a";

/** Tailwind classes per status, applied to the badge pill. */
export const STATUS_BADGE: Record<TransactionStatus, string> = {
  authorized: "bg-yellow-100 text-yellow-800",
  settled: "bg-green-100 text-green-800",
  voided: "bg-gray-200 text-gray-700",
  // Terminal, non-recoverable outcomes reconciled from Braintree (OMG-2246).
  settlement_declined: "bg-red-100 text-red-800",
  expired: "bg-orange-100 text-orange-800",
  gateway_rejected: "bg-red-100 text-red-800",
  // Post-settlement flip from the refund-reconcile poll (OMG-2504).
  refunded: "bg-blue-100 text-blue-800",
  // Imported Broadleaf-era rows (OMG-2574): history, never actionable.
  legacy_unsettled: "bg-gray-100 text-gray-600",
  legacy_unknown: "bg-gray-100 text-gray-600",
};

/** Class list for the status pill. Unknown statuses (a BE that has shipped a new
 *  one before this app has) fall back to neutral rather than `undefined`. */
export const statusBadgeClass = (status: TransactionStatus): string =>
  `px-2 py-0.5 rounded-full text-xs font-medium ${
    STATUS_BADGE[status] ?? "bg-gray-200 text-gray-700"
  }`;

/** Pill shown next to a legacy row's id: it came from the old Java site's
 *  MySQL and opens the read-only legacy detail, not the live one. */
export const LEGACY_BADGE_CLASS =
  "ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800";

export const isLegacyRow = (
  row: Pick<AdminTransactionRow, "source">,
): boolean => row.source === "legacy";

/** Detail route for a listing row. Legacy rows have their own read-only page
 *  (OMG-2574): the live detail endpoint reads pending_settlement only and
 *  404s on a legacy id. */
export const transactionDetailPath = (
  row: Pick<AdminTransactionRow, "source" | "transactionId">,
): string =>
  isLegacyRow(row)
    ? `/transactions/legacy/${encodeURIComponent(row.transactionId)}`
    : `/transactions/${encodeURIComponent(row.transactionId)}`;

/** Format a transaction amount in its own currency.
 *
 *  Legacy rows carry no amount or currency at all (Braintree-only data, not
 *  imported): render the placeholder rather than "null null".
 *
 *  `pending_settlement.currency` is unconstrained `text`, and `Intl.NumberFormat`
 *  throws RangeError on a value that isn't a well-formed currency code — which
 *  unwinds the whole page, not just the row. Falling back to `amount CODE` keeps
 *  a bad row readable instead of blanking the screen. Now that all call sites
 *  share this function, the guard covers all of them. pg serializes numeric as
 *  a string, so accept that too. */
export const formatAmount = (
  amount: number | string | null | undefined,
  currency: string | null | undefined,
): string => {
  if (amount == null || currency == null) return MISSING_VALUE;
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (Number.isNaN(value)) return `${amount} ${currency}`;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${amount} ${currency}`;
  }
};

/** Apply a date-fns formatter to a nullable ISO instant. Legacy rows with no
 *  PAYMENT_AUTHORIZATION match have no authorizedAt; `new Date(null)` would be
 *  the epoch and render as "over 56 years ago". */
export const formatNullableDate = (
  iso: string | null | undefined,
  formatter: (date: Date) => string,
): string => {
  if (!iso) return MISSING_VALUE;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? MISSING_VALUE : formatter(date);
};
